export type GuessResult = {
    title: string;
    titleCorrect: boolean;
    artistGuesses: string[];
    artistsCorrect: string[];
    artistsRemaining: string[];
    pointsGained: number;
};

const normalize = (str: string) =>
    str.toLowerCase().replace(/^the\s+/, "").replace(/[^a-z0-9\s]/g, '').trim();

const stripParentheticals = (str: string) =>
    str.replace(/\(.*?\)/g, '').replace(/\[.*?\]/g, '').trim();

function levenshtein(a: string, b: string): number {
    const dp = Array.from({ length: a.length + 1 }, (_, i) =>
        Array.from({ length: b.length + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0))
    );
    for (let i = 1; i <= a.length; i++)
        for (let j = 1; j <= b.length; j++)
            dp[i][j] =
                a[i - 1] === b[j - 1]
                    ? dp[i - 1][j - 1]
                    : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    return dp[a.length][b.length];
}

const LEVENSHTEIN_THRESHOLD = 2;

export const isCorrectGuess = (guess: string, answer: string, gameMode: string = "track"): boolean => {
    const normalizedGuess = normalize(guess);
    const normalizedAnswer = normalize(answer);
    const strippedAnswer = normalize(stripParentheticals(answer));

    if (gameMode === "artist") {
        if (normalizedGuess === normalizedAnswer) return true;
        const lastWord = normalizedAnswer.split(" ").at(-1) ?? "";
        if (normalizedGuess === lastWord) return true;
        if (normalizedAnswer.length > 4 && levenshtein(normalizedGuess, normalizedAnswer) < LEVENSHTEIN_THRESHOLD) return true;
        if (lastWord.length > 4 && levenshtein(normalizedGuess, lastWord) < LEVENSHTEIN_THRESHOLD) return true;
        return false;
    }

    if (normalizedGuess === normalizedAnswer || normalizedGuess === strippedAnswer) return true;

    if (normalizedAnswer.includes('/')) {
        const parts = normalizedAnswer.split('/').map(p => p.trim());
        for (const part of parts) {
            const normalizedPart = normalize(part);
            if (normalizedGuess === normalizedPart) return true;
            if (normalizedPart.length > 4 && levenshtein(normalizedGuess, normalizedPart) < LEVENSHTEIN_THRESHOLD) return true;
        }
    }

    if (normalizedAnswer.length > 4 && levenshtein(normalizedGuess, normalizedAnswer) < LEVENSHTEIN_THRESHOLD) return true;
    return false;
};

export const evaluateChartGuess = (
    guess: string,
    correctAnswer: string,
    field: 'title' | 'artist'
): { correct: boolean; pointsGained: number } => {
    if (!guess || !correctAnswer) return { correct: false, pointsGained: 0 };
    const isCorrect = isCorrectGuess(guess, correctAnswer, field);
    const pointsGained = isCorrect ? (field === 'title' ? 5 : 3) : 0;
    return { correct: isCorrect, pointsGained };
};

// Multi-artist version — awards proportional points per matched artist
export const evaluateArtistGuess = (
    guess: string,
    allArtists: string[],
    alreadyCorrect: string[]
): { matchedArtist: string | null; pointsGained: number } => {
    if (!guess || allArtists.length === 0) return { matchedArtist: null, pointsGained: 0 };

    const remaining = allArtists.filter(a => !alreadyCorrect.includes(a));
    const match = remaining.find(a => isCorrectGuess(guess, a, 'artist')) ?? null;
    const pointsGained = match ? (3 / allArtists.length) : 0;

    return { matchedArtist: match, pointsGained };
};

export const calculateTotalScore = (guesses: { [key: number]: GuessResult }): number => {
    return Object.values(guesses).reduce((sum, guess) => sum + guess.pointsGained, 0);
};